<script lang="ts">
  import { InboxWorkflowPreconfigurationBox } from "@kontextwerk/control-web-frame"
  import {
    type InboxWorkflowJson,
    type InboxWorkflowPreconfigurationProps,
  } from "@kontextwerk/control-web-frame/inbox-workflow/contract"
  import {
    notifyInboxWorkflowPreconfigurationApplyResult,
    resolveInboxWorkflowPreconfigurationTargets,
  } from "@kontextwerk/control-web-frame"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import type { LetterDeliveryDecisionDraft, LetterDeliveryDecisionRow } from "../../contract"
  import {
    buildLetterDeliveryModeOptions,
    type LetterDeliveryMode,
  } from "../../_shared/_logic/delivery-mode-options"
  import { automaticDeliveryUnavailableReason } from "../../_shared/_logic/02-decide-appointment-letter-delivery-payload"
  import {
    isKeepPreconfigurationChoice,
    withKeepPreconfigurationOption,
    type PreconfigurationChoice,
  } from "../../../../../../../../../../../../_shared/_logic/preconfiguration-keep"

  let {
    rows,
    selectedIds,
    patchRows,
    disabled = false,
    automaticLetterDeliveryAllowed = true,
    automaticLetterDeliveryDisabledReason = null,
  }: InboxWorkflowPreconfigurationProps<LetterDeliveryDecisionRow, LetterDeliveryDecisionDraft> & {
    automaticLetterDeliveryAllowed?: boolean
    automaticLetterDeliveryDisabledReason?: string | null
  } = $props()

  let selectedDeliveryMode = $state<PreconfigurationChoice<LetterDeliveryMode> | null>(null)
  const automaticDefaultAvailable = $derived(
    rows.some(
      (row) =>
        !row.disabled &&
        !automaticDeliveryUnavailableReason(
          row,
          automaticLetterDeliveryAllowed,
          automaticLetterDeliveryDisabledReason,
        ),
    ),
  )
  const deliveryMode = $derived(
    selectedDeliveryMode ?? (automaticDefaultAvailable ? "automatic" : "manual"),
  )
  let lastApply = $state<{
    mode: PreconfigurationChoice<LetterDeliveryMode>
    applied: number
    skipped: number
  } | null>(null)

  const modeUnavailableReason = $derived(
    deliveryMode === "automatic" && !automaticLetterDeliveryAllowed
      ? automaticLetterDeliveryDisabledReason || "Automatische Zustellung ist nicht freigegeben."
      : null,
  )

  const deliveryModeOptions = $derived(
    withKeepPreconfigurationOption(
      buildLetterDeliveryModeOptions({
        automaticLetterDeliveryAllowed,
        automaticLetterDeliveryDisabledReason,
      }),
    ),
  )

  const targetResolution = $derived(
    resolveInboxWorkflowPreconfigurationTargets({
      rows,
      selectedIds,
      getId: (row) => row.id,
      isEligible: (row) =>
        !row.disabled &&
        (deliveryMode !== "automatic" ||
          !automaticDeliveryUnavailableReason(
            row,
            automaticLetterDeliveryAllowed,
            automaticLetterDeliveryDisabledReason,
          )),
    }),
  )

  const apply = () => {
    if (disabled || modeUnavailableReason) return

    if (!isKeepPreconfigurationChoice(deliveryMode)) {
      patchRows(targetResolution.targetRows, {
        deliveryMode,
      } as Partial<LetterDeliveryDecisionDraft> & InboxWorkflowJson)
    }

    lastApply = {
      mode: deliveryMode,
      applied: targetResolution.targetRows.length,
      skipped: targetResolution.skippedRows.length,
    }
    notifyInboxWorkflowPreconfigurationApplyResult({
      total: targetResolution.targetRows.length + targetResolution.skippedRows.length,
      applied: targetResolution.targetRows.length,
      skipped: targetResolution.skippedRows.length,
      reason:
        deliveryMode === "automatic"
          ? "Automatische Zustellung ist für einzelne Terminbriefe nicht verfügbar."
          : undefined,
    })
  }
</script>

<InboxWorkflowPreconfigurationBox
  selectedCount={selectedIds.length}
  targetCount={targetResolution.targetRows.length}
  disabled={disabled || Boolean(modeUnavailableReason)}
  onApply={apply}
>
  <div class="preconfiguration-grid">
    <Input
      type="select"
      label="Zustellart"
      bind:value={() => deliveryMode, (value) => (selectedDeliveryMode = value)}
      brightBackground={false}
      surfaceTone="dark"
      options={deliveryModeOptions}
    />
  </div>
  <p>
    Aktuelle Auswahl: {targetResolution.targetRows.length} anwendbar,
    {targetResolution.skippedRows.length} ausgeschlossen.
  </p>
  {#if modeUnavailableReason}
    <p role="alert">{modeUnavailableReason} Bitte Berechtigung prüfen oder Händisch wählen.</p>
  {/if}
</InboxWorkflowPreconfigurationBox>

{#if lastApply}
  <p role="status" class="apply-result">
    Letzte Massenanwendung: {lastApply.applied}
    {isKeepPreconfigurationChoice(lastApply.mode)
      ? "unverändert gelassen"
      : `auf ${lastApply.mode === "automatic" ? "Automatisch" : "Händisch"} gesetzt`};
    {lastApply.skipped} ausgeschlossen und unverändert.
  </p>
{/if}

<style>
  .apply-result {
    margin: 0.75rem 0;
    overflow-wrap: anywhere;
  }

  .preconfiguration-grid {
    padding-top: 1rem;
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(12rem, 1fr);
    max-width: 32rem;
  }
</style>
